import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDebounceValue } from "usehooks-ts";
import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import Config from "@via/utils/config.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  maxLengthValidation,
  minLengthValidation,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import {
  useCheckInviteUrl,
  useUpdateOrganizationSettings,
} from "@via/pages/organization/organization.api.tsx";

const SLUG_PATTERN = /^[a-zA-Z0-9-]+$/;
const SLUG_MAX_LENGTH = 255;

const buildInviteUrl = (slug: string): string => {
  const basename = Config.ROUTER_BASENAME || "";
  return `${window.location.origin}${basename}${ROUTES.JOIN}/${encodeURIComponent(slug.trim())}`;
};

interface InviteUrlFormPayload {
  slug: string;
}

interface InviteUrlFormProps {
  organizationId: string;
  initialSlug: string | null;
}

const InviteUrlForm: React.FC<InviteUrlFormProps> = ({
  organizationId,
  initialSlug,
}) => {
  const { handleSubmit, control, watch, reset } = useForm<InviteUrlFormPayload>(
    {
      mode: "onChange",
      defaultValues: { slug: initialSlug ?? "" },
    }
  );

  useEffect(() => {
    reset({ slug: initialSlug ?? "" });
  }, [initialSlug, reset]);

  const slugValue = watch("slug");
  const trimmedSlug = slugValue.trim();
  const [debouncedSlug] = useDebounceValue(trimmedSlug, 400);

  const {
    data: checkResult,
    isLoading: isCheckLoading,
    isFetching: isCheckFetching,
    isError: isCheckError,
  } = useCheckInviteUrl(organizationId, debouncedSlug);

  const { mutate: updateSettings, isPending: isUpdatePending } =
    useUpdateOrganizationSettings();

  const isAvailable = checkResult?.available === true;
  const isChecking =
    isCheckLoading || (isCheckFetching && trimmedSlug === debouncedSlug);
  const fullUrl = buildInviteUrl(slugValue);
  const canSave =
    (isAvailable || trimmedSlug === (initialSlug ?? "")) && !isUpdatePending;

  const onSubmit = (data: InviteUrlFormPayload) => {
    if (!canSave) return;
    updateSettings({
      requestParams: { id: organizationId },
      requestBody: { slug: data.slug.trim() },
    });
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(onSubmit)(e);
      }}>
      <div>
        <FormInputComponent
          control={control}
          name="slug"
          label="Invite link path"
          showRequiredField
          placeholder="e.g. organization-name"
          maxLength={SLUG_MAX_LENGTH}
          validations={{
            ...requiredValidation("Invite link path"),
            ...minLengthValidation(1, "Enter at least 1 character."),
            ...maxLengthValidation(
              SLUG_MAX_LENGTH,
              `Must be at most ${SLUG_MAX_LENGTH} characters.`
            ),
            pattern: {
              value: SLUG_PATTERN,
              message:
                "Use only letters, numbers, and hyphens (1–255 characters).",
            },
          }}
        />
        <div className="mt-1 flex items-center gap-2">
          {isChecking ? (
            <Spinner loading />
          ) : isCheckError ? (
            <span className="text-sm text-amber-600">
              Could not verify availability. Please try again.
            </span>
          ) : trimmedSlug.length > 0 && SLUG_PATTERN.test(trimmedSlug) ? (
            isAvailable ? (
              <span className="text-sm text-green-600">Available</span>
            ) : (
              <span className="text-sm text-red-600">Not available</span>
            )
          ) : null}
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Full invite URL
        </label>
        <CopyableText value={fullUrl || "—"} showValue={true} />
      </div>

      <div className="flex flex-shrink-0 justify-end">
        <button
          type="submit"
          disabled={!canSave}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
          <Spinner loading={isUpdatePending}>Save</Spinner>
        </button>
      </div>
    </form>
  );
};

export default InviteUrlForm;
